import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import ViewSlider, { ViewProps } from "react-view-slider";
import { useFetchAllUserProducts } from "../../../api/products/hooks/useFetchAllUserProducts";
import { useFetchFeaturedArtistReleaseQuery } from "../../../api/releases/hooks/useFetchFeaturedReleaseQuery";
import {
  artistReleaseTabViewAtom,
  ReleasesTabView,
  selectedReleaseIdAtom,
} from "../../../atoms/profileScreen/artistReleaseTabView";
import { editMode } from "../../../atoms/profileScreenEdit";
import { activeUserAtom } from "../../../atoms/user/activeUserAtom";
import { QUERY_KEYS } from "../../../constants/queryKeys";
import { useSetPlayList } from "../../../hooks/audioPlayerHooks/useGetFooterPlayerRef";
import { useGetUserProfile } from "../../../hooks/profileScreenHooks/useGetUserProfile";
import { FooterFileTrackType } from "../../../store/actions/abPlayerStore";
import { useAppSelector } from "../../../store/hooks";
import convertArtistReleaseToPlaylistTrack from "../../../store/models/playListTrack/helpers/convertArtistReleaseToPlaylistTrack";
import { ReleaseTrack } from "../../../store/models/release";
import { FeaturedArtistRelease } from "../FeaturedArtistRelease/FeaturedArtistRelease";
import { FeaturedReleaseProducts } from "../FeaturedReleaseProducts/FeaturedReleaseProducts";
import { ProductCheckoutFooter } from "../ProductCheckoutFooter/ProductCheckoutFooter";
import { AddReleaseView } from "./AddReleaseView";
import { ReleaseDetailView } from "./ReleaseDetailView";
import { ReleaseOverview } from "./ReleaseOverview";

export const ArtistReleaseTabView = () => {
  const [currentView, setView] = useAtom(artistReleaseTabViewAtom);
  const [selectedReleaseId, setSelectedReleaseId] = useAtom(
    selectedReleaseIdAtom,
  );
  const queryClient = useQueryClient();
  const { isFooterPlaying } = useAppSelector((state) => state.abPlayerStore);
  const { username } = useParams<{ username: string }>();
  const activeUser = useAtomValue(activeUserAtom);
  const { data: userData } = useGetUserProfile(username);
  const { data: release, isPending: isReleaseLoading } =
    useFetchFeaturedArtistReleaseQuery(userData?.id);
  const currentUserIsArtistOnRelease =
    release?.artist_user_id === activeUser?.id;
  const {
    data: productsData,
    isPending: isProductsFetching,
    isSuccess: isProductsFetchingSuccess,
  } = useFetchAllUserProducts(userData?.id);

  useEffect(() => {
    return () => {
      setSelectedReleaseId(null);
      setView(ReleasesTabView.overview);
    };
  }, [setSelectedReleaseId, setView]);

  const setPlayList = useSetPlayList();

  const handleSettingUpPlaylist = useCallback(
    (releaseId: number) => {
      if (isFooterPlaying) return;
      const tracks =
        (queryClient.getQueryData([
          QUERY_KEYS.FETCH_ARTIST_RELEASE_TRACKS,
          releaseId,
        ]) as ReleaseTrack[] | undefined) ?? [];

      if (!tracks.length) return;
      setPlayList({
        tracks: convertArtistReleaseToPlaylistTrack(tracks).filter(
          (releaseTrack) => !releaseTrack.paymentRequiredForPlayback,
        ),
        index: 0,
        playOnLoad: true,
        playlistId: releaseId,
        footerFileTrackType: FooterFileTrackType.RELEASE,
      });
    },
    [isFooterPlaying, queryClient, setPlayList],
  );

  const onReleaseClick = useCallback(
    (releaseId?: number) => {
      if (!releaseId) return;
      handleSettingUpPlaylist(releaseId);
      setSelectedReleaseId(releaseId);
      setView(ReleasesTabView.detail);
    },
    [handleSettingUpPlaylist, setSelectedReleaseId, setView],
  );

  // Products associated with the Featured Release
  const releaseProducts = useMemo(() => {
    return (productsData || []).filter(
      (product) => product.release_id === selectedReleaseId,
    );
  }, [productsData, selectedReleaseId]);

  const ctaText = useMemo(() => {
    const now = new Date();
    if (currentUserIsArtistOnRelease && !editMode) {
      return "Play Project";
    }
    if (isProductsFetching) {
      return "...";
    }
    const userBoughtSomeProductOnRelease = releaseProducts.some((product) => {
      // filter out ended products
      if (product.published_end && new Date(product.published_end) < now) {
        return false;
      }
      return product.product_purchased_by_current_user;
    });
    if (
      isProductsFetchingSuccess &&
      releaseProducts.length > 0 &&
      !userBoughtSomeProductOnRelease
    ) {
      return "Buy Project";
    }
    return "Play Project";
  }, [
    releaseProducts,
    currentUserIsArtistOnRelease,
    isProductsFetching,
    isProductsFetchingSuccess,
  ]);

  // TODO: Clean this up after the smino livestream.
  const userIsNotSmino = userData?.username !== "smino";

  const renderView = ({ index }: ViewProps) => {
    switch (index) {
      case ReleasesTabView.overview:
        return (
          <ReleaseOverview onReleaseClick={onReleaseClick}>
            {userIsNotSmino && (
              <Box
                sx={(theme) => ({
                  // This accounts for the box shadow of the feature image
                  [theme.breakpoints.up("md")]: {
                    padding: "16px 0 0 16px",
                  },

                  [theme.breakpoints.down("sm")]: {
                    padding: "16px 0 0 0",
                  },

                  [theme.breakpoints.up("sm")]: {
                    padding: "16px 16px 0 16px",
                  },
                })}
              >
                {release && !isReleaseLoading && (
                  <FeaturedArtistRelease
                    release={release}
                    onClick={() => {
                      onReleaseClick(release.id);
                    }}
                    ctaText={ctaText}
                  />
                )}
              </Box>
            )}
            <FeaturedReleaseProducts featuredReleaseId={release?.id} />
          </ReleaseOverview>
        );

      case ReleasesTabView.detail:
        if (!selectedReleaseId) {
          return <AddReleaseView />;
        }
        return (
          <ReleaseDetailView
            hidePurchaseButton={false}
            navigateBack={() => {
              setView(ReleasesTabView.overview);
            }}
            releaseId={selectedReleaseId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <ViewSlider
        onSlideTransitionEnd={() => {
          if (currentView === ReleasesTabView.overview) {
            setSelectedReleaseId(null);
          }
        }}
        numViews={2}
        keepViewsMounted
        renderView={renderView}
        animateHeight
        activeView={currentView}
      />
      {isProductsFetchingSuccess && productsData && productsData.length > 0 && (
        <ProductCheckoutFooter />
      )}
    </Box>
  );
};
